import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, MapPin, Loader2, AlertCircle, ArrowRight, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogIndexPage() {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") || "";
  const activeCity = params.get("city") || "";
  const activeQuery = params.get("q") || "";

  const [posts, setPosts] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [q, setQ] = React.useState(activeQuery);

  React.useEffect(() => {
    // SEO — page title + meta description
    document.title = "Raanah Blog · UK van removals, courier & moving guides";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.name = "description";
      document.head.appendChild(m);
      return m;
    })();
    meta.content = "Guides, checklists and tips for house removals, courier deliveries and van hire across the UK. Written for customers and drivers.";
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog-categories`);
        setCategories(data.categories || []);
      } catch { /* categories fetch is best-effort */ }
    })();
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const searchParams = new URLSearchParams();
        if (activeCategory) searchParams.set("category", activeCategory);
        if (activeCity) searchParams.set("city", activeCity);
        if (activeQuery) searchParams.set("q", activeQuery);
        searchParams.set("limit", "30");
        const { data } = await axios.get(`${API}/blog?${searchParams.toString()}`);
        if (cancelled) return;
        setPosts(data.items || []);
        setTotal(data.total || 0);
      } catch (e) {
        if (!cancelled) setError("Failed to load posts. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCategory, activeCity, activeQuery]);

  const applyFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    applyFilter("q", q.trim());
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="blog-index-page">
      <header className="border-b border-[#0A0A0A]/10 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="font-display text-xl font-bold tracking-tight text-[#0A0A0A]">Raanah</Link>
          <nav className="text-sm">
            <Link to="/" className="text-[#475569] hover:text-[#0A0A0A]">← Back to Raanah</Link>
          </nav>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-14">
        <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Raanah insights</p>
        <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0A0A0A]">
          Moving, courier & van hire — the honest guides.
        </h1>
        <p className="mt-4 max-w-2xl text-base text-[#475569]">
          Real-world advice from Luton van drivers and removals customers across the UK. No fluff, no filler.
        </p>

        {/* Search + category filters */}
        <div className="mt-10 space-y-4">
          <form onSubmit={submitSearch} className="flex items-center gap-2 max-w-xl">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search removals, courier, packing, tips…"
                data-testid="blog-search-input"
                className="w-full pl-10 pr-4 py-3 border border-[#0A0A0A]/15 bg-white text-sm focus:outline-none focus:border-[#1E4D55]"
              />
            </div>
            <button
              type="submit"
              data-testid="blog-search-submit"
              className="bg-[#1E4D55] hover:bg-[#163C42] text-white px-4 py-3 text-sm font-semibold"
            >
              Search
            </button>
          </form>

          <div className="flex flex-wrap gap-2" data-testid="blog-category-filter">
            <button
              onClick={() => applyFilter("category", "")}
              className={`text-xs uppercase tracking-[0.14em] font-bold px-3 py-1.5 border transition-colors ${!activeCategory ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#0A0A0A]/20 hover:border-[#0A0A0A]"}`}
              data-testid="blog-category-all"
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.key}
                onClick={() => applyFilter("category", c.key)}
                data-testid={`blog-category-${c.key}`}
                className={`text-xs uppercase tracking-[0.14em] font-bold px-3 py-1.5 border transition-colors ${activeCategory === c.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#0A0A0A]/20 hover:border-[#0A0A0A]"}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {activeCity && (
            <div className="text-xs text-[#475569] inline-flex items-center gap-1.5">
              <MapPin size={12} /> Filtered by city: <strong>{activeCity}</strong>
              <button onClick={() => applyFilter("city", "")} className="ml-2 underline">clear</button>
            </div>
          )}
        </div>

        {/* Posts */}
        <div className="mt-10">
          {loading ? (
            <p className="text-sm text-[#475569] inline-flex items-center gap-2" data-testid="blog-loading"><Loader2 size={14} className="animate-spin" /> Loading…</p>
          ) : error ? (
            <div className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3 flex items-start gap-2" data-testid="blog-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-white border border-dashed border-[#0A0A0A]/20 p-12 text-center" data-testid="blog-empty">
              <p className="font-display text-xl font-bold text-[#0A0A0A]">No posts yet</p>
              <p className="mt-2 text-sm text-[#475569]">Check back soon — we&apos;re writing new guides every week.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
              {posts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}
          {!loading && total > posts.length && (
            <p className="mt-6 text-xs text-[#475569]">Showing {posts.length} of {total} posts.</p>
          )}
        </div>
      </section>

      <footer className="border-t border-[#0A0A0A]/10 bg-white mt-24">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-[#475569] flex items-center justify-between">
          <span>© {new Date().getFullYear()} Raanah. All rights reserved.</span>
          <Link to="/" className="hover:text-[#0A0A0A]">Back home →</Link>
        </div>
      </footer>
    </div>
  );
}

function PostCard({ post }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white border border-[#0A0A0A]/10 hover:border-[#1E4D55] transition-colors group"
      data-testid={`blog-card-${post.slug}`}
    >
      <Link to={`/blog/${post.slug}`} className="block">
        {post.hero_image_url && (
          <div className="aspect-[16/9] bg-[#F5F5F5] overflow-hidden">
            <img src={post.hero_image_url} alt={post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          </div>
        )}
        <div className="p-5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#1E4D55]">{(post.category || "").replace(/_/g, " ")}</span>
            {post.city && (
              <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569] inline-flex items-center gap-1">
                <MapPin size={10} /> {post.city}
              </span>
            )}
          </div>
          <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-[#0A0A0A] leading-snug">
            {post.title}
          </h3>
          <p className="mt-2 text-sm text-[#475569] line-clamp-3">{post.meta_description}</p>
          <p className="mt-4 text-xs font-semibold text-[#1E4D55] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
            Read <ArrowRight size={12} />
          </p>
        </div>
      </Link>
    </motion.article>
  );
}
